import { useToolOwnership } from "pages/Meetings/call/shared/toolOwnership"
import { useCallback, useEffect, useRef, useState } from 'react'
import { mentorshipContinuity, type ContinuityRecap, type MentorshipRelationship } from 'services/api/mentorshipContinuityClient'

const CHANGE_EVENT = 'wisefriend:mentorship-continuity-changed'

export function notifyContinuityChanged(cohortId: string) {
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: cohortId }))
}

export function useMentorshipRelationships(cohortId?: string, enabled = true) {
  const {key: ownerKey, capture} = useToolOwnership(`${cohortId}`)
  const [state, setState] = useState<{
    cohortId?: string
    ownerKey?: string
    relationships: MentorshipRelationship[]
    loading: boolean
    error: string | null
  }>({ relationships: [], loading: Boolean(cohortId && enabled), error: null })
  const request = useRef(0)

  const refresh = useCallback(async () => {
    const owns = capture()
    const current = ++request.current
    if (!cohortId || !enabled) {
      setState({ ownerKey, cohortId, relationships: [], loading: false, error: null })
      return
    }
    setState(previous => ({
      ownerKey, cohortId,
      relationships: previous.cohortId === cohortId && previous.ownerKey === ownerKey ? previous.relationships : [],
      loading: true,
      error: null,
    }))
    try {
      const relationships = await mentorshipContinuity.list(cohortId)
      if (owns() && current === request.current) setState({ ownerKey, cohortId, relationships, loading: false, error: null })
    } catch (error) {
      if (!owns() || current !== request.current) return
      // Clear private content on any failed access check, including revoked assignments.
      setState({ ownerKey, cohortId, relationships: [], loading: false, error: 'We couldn’t load these relationship notes. Your access may have changed, or the connection may be unavailable.' })
    }
  }, [cohortId, enabled, ownerKey, capture])

  useEffect(() => {
    void refresh()
    const listener = (event: Event) => {
      if ((event as CustomEvent<string>).detail === cohortId) void refresh()
    }
    window.addEventListener(CHANGE_EVENT, listener)
    return () => {
      request.current += 1
      window.removeEventListener(CHANGE_EVENT, listener)
    }
  }, [cohortId, refresh])

  const updateRelationship = useCallback((relationship: MentorshipRelationship) => {
    setState(previous => previous.ownerKey !== ownerKey || previous.cohortId !== relationship.cohort_id ? previous : {
      ...previous,
      relationships: previous.relationships.map(row => row.id === relationship.id ? relationship : row),
    })
  }, [ownerKey])

  const visible = enabled && state.cohortId === cohortId && state.ownerKey === ownerKey
  return {
    relationships: visible ? state.relationships : [],
    loading: Boolean(cohortId && enabled) && (!visible || state.loading),
    error: visible ? state.error : null,
    refresh,
    updateRelationship,
  }
}

export function hasUnconfirmedPairAttendance(recap?: ContinuityRecap): boolean {
  return recap?.unavailable_reason === 'pair_attendance_unconfirmed'
    && recap.source_kind === 'group' && !recap.in_progress
    && ['partial', 'failed'].includes(recap.status) && !recap.summary
}

export function getContinuityCue(relationship: MentorshipRelationship): string {
  if (!relationship.is_current) return 'Past relationship · View notes'
  const latest = [...relationship.recaps].sort((a, b) => (b.meeting_start_time || b.created_at).localeCompare(a.meeting_start_time || a.created_at))[0]
  const count = relationship.items.filter(item => item.kind === 'topic' && item.status === 'open' && !item.is_removed).length
  if (hasUnconfirmedPairAttendance(latest)) return count ? `${count} open ${count === 1 ? 'topic' : 'topics'} · For next time` : 'For next time'
  if (latest?.status === 'pending') return count ? `${count} open ${count === 1 ? 'topic' : 'topics'} · Recap processing` : 'Recap processing'
  if (latest?.status === 'failed') return 'Recap unavailable · View notes'
  if (latest?.status === 'partial') return 'Partial recap · View notes'
  if (count) return `${count} open ${count === 1 ? 'topic' : 'topics'} · For next time`
  return latest ? 'Recap ready · For next time' : 'For next time'
}
